import { useId, useRef, useState } from "react";
import { useFetcher } from "react-router";
import type { EventPart } from "@repo/shared-types";
import { partLabel, type PartBounds } from "@/lib/event-form";
import { toWallTime } from "@/lib/times";
import { CogMenu, MenuButton, MenuLink } from "@/components/cog-menu";
import { OptionalTime } from "@/components/event-fields";
import { ErrorNote, ProblemProvider, useFormProblem, useProblem } from "@/components/form";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog, Modal, useClosedWhenDone, type Outcome } from "@/components/modal";

/**
 * Running an event, behind cogs (decided with Tiago, 7 October): one beside
 * its title, one on each part's card, for the owner and admins holding
 * manage_events. A part's Edit and Delete used to be buttons in its card,
 * right above the viewer's own result's Edit and Undo, and read as one
 * thing. Each opens a dialog that posts to the event's action by fetcher,
 * and closes once it answers without an error.
 */

/** The event's cog: Edit event, on its own page, and Delete event…, asked first. */
export function EventMenu({ editTo }: { editTo: string }) {
  const [deleting, setDeleting] = useState(false);
  return (
    <>
      <CogMenu label="Event menu">
        {(close) => (
          <>
            <MenuLink to={editTo} label="Edit event" />
            <MenuButton
              danger
              onClick={() => {
                close();
                setDeleting(true);
              }}
            >
              Delete event…
            </MenuButton>
          </>
        )}
      </CogMenu>
      {deleting && (
        <ConfirmDialog title="Delete this event?" fields={{ intent: "delete-event" }} confirm="Delete event" onClose={() => setDeleting(false)}>
          Deleting it removes it and its parts for everyone. It cannot be undone.
        </ConfirmDialog>
      )}
    </>
  );
}

/**
 * A part's cog: Edit part — its title, start, description and deadline —
 * and Delete part…, asked first. How a part is scored is not here, and never
 * will be.
 */
export function PartMenu({
  part,
  index,
  zone,
  bounds,
}: {
  part: EventPart;
  index: number;
  zone: string;
  /** Its event's start and deadline, which its own dates stay inside (0083). */
  bounds: PartBounds;
}) {
  const [dialog, setDialog] = useState<"edit" | "delete" | null>(null);
  const label = partLabel(index);
  return (
    <>
      <CogMenu label={`Part ${label} menu`} compact>
        {(close) => (
          <>
            <MenuButton
              onClick={() => {
                close();
                setDialog("edit");
              }}
            >
              Edit part
            </MenuButton>
            <MenuButton
              danger
              onClick={() => {
                close();
                setDialog("delete");
              }}
            >
              Delete part…
            </MenuButton>
          </>
        )}
      </CogMenu>
      {dialog === "edit" && <EditPartDialog part={part} label={label} zone={zone} bounds={bounds} onClose={() => setDialog(null)} />}
      {dialog === "delete" && (
        <ConfirmDialog
          title={`Delete part ${label}. ${part.title}?`}
          fields={{ intent: "delete-part", part: part.id }}
          confirm="Delete part"
          onClose={() => setDialog(null)}
        >
          It goes from the event for everyone, and its leaderboard with it. Results already recorded
          on it stay in each athlete's log.
        </ConfirmDialog>
      )}
    </>
  );
}

/**
 * Edit part: what may change about a part, posted by fetcher. The action says
 * "Part updated." by a toast, since the dialog closing is all the card shows.
 */
function EditPartDialog({
  part,
  label,
  zone,
  bounds,
  onClose,
}: {
  part: EventPart;
  label: string;
  zone: string;
  bounds: PartBounds;
  onClose: () => void;
}) {
  const fetcher = useFetcher<Outcome>();
  useClosedWhenDone(fetcher, onClose);
  const busy = fetcher.state !== "idle";
  const form = useRef<HTMLFormElement>(null);
  const shown = useFormProblem(fetcher.data, form);
  return (
    <Modal title={`Edit part ${label}`} onClose={onClose}>
      <fetcher.Form ref={form} method="post" className="space-y-5" noValidate onChange={shown.onChange}>
        <input type="hidden" name="intent" value="update-part" />
        <input type="hidden" name="part" value={part.id} />
        <ProblemProvider value={shown.problem}>
          <EditPartFields part={part} zone={zone} bounds={bounds} />
        </ProblemProvider>
        <p className="text-sm text-muted-foreground">
          How a part is scored, and whether it is verifiable, cannot change. To score it
          differently, delete it and add another.
        </p>
        {shown.general && <ErrorNote message={shown.general} />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="lg" disabled={busy}>
            Save part
          </Button>
        </div>
      </fetcher.Form>
    </Modal>
  );
}

/** What may change about a part: each box outlined, and why under it, when it is refused. */
function EditPartFields({ part, zone, bounds }: { part: EventPart; zone: string; bounds: PartBounds }) {
  const id = useId();
  const titleProblem = useProblem("title");
  return (
    <>
      <Field data-invalid={titleProblem ? true : undefined}>
        <FieldLabel htmlFor={`${id}-title`}>Title</FieldLabel>
        <Input
          id={`${id}-title`}
          name="title"
          required
          maxLength={120}
          defaultValue={part.title}
          aria-invalid={titleProblem ? true : undefined}
        />
        {titleProblem && <FieldError>{titleProblem}</FieldError>}
      </Field>
      {/* Without one, the part starts with the event: closed, it sends none, which clears it. */}
      <OptionalTime
        name="startsAt"
        label="Starts"
        add="+ Add a start time"
        remove="Remove the start time"
        initial={part.startsAt ? toWallTime(part.startsAt, zone) : ""}
      />
      <Field>
        <FieldLabel htmlFor={`${id}-description`}>Description</FieldLabel>
        <Textarea
          id={`${id}-description`}
          name="description"
          rows={3}
          maxLength={10_000}
          defaultValue={part.description ?? ""}
        />
      </Field>
      {/* A missing deadline box leaves the deadline alone, so closing this one clears it.
          While the event has a deadline the part keeps one, at or before it: no × (0083). */}
      {part.hasLeaderboard && (
        <OptionalTime
          name="deadline"
          label="Deadline"
          add="+ Add a deadline"
          remove="Remove the deadline"
          initial={part.deadline ? toWallTime(part.deadline, zone) : bounds.deadline}
          min={part.startsAt ? toWallTime(part.startsAt, zone) : bounds.starts}
          max={bounds.deadline || undefined}
          fixed={bounds.deadline !== ""}
          hint={
            bounds.deadline
              ? "Move it later to turn late results back into on-time ones, no later than the event's deadline. It never moves back into the past."
              : "Move it later to turn late results back into on-time ones. It never moves back into the past."
          }
          clears
        />
      )}
    </>
  );
}
